import { useState, useEffect, useCallback } from 'react';
import { plats as mockPlats } from '@/mocks/plats';
import { fetchAllPlats, createPlat, deletePlat, ApiPlat } from '@/services/api';

// SVG Icons
const AddIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
);
const ErrorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
);
const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
);
const LoaderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
);
const DatabaseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5V19A9 3 0 0 0 21 19V5"/><path d="M3 12A9 3 0 0 0 21 12"/></svg>
);
const ImageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
);
const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
);
const FileCodeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><polyline points="9 13 15 13"/><polyline points="9 17 15 17"/></svg>
);
const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
);
const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
);

type NutriScore = 'A' | 'B' | 'C' | 'D' | 'E';
type Categorie = 'poisson' | 'viande' | 'volaille' | 'vegetarien';

const emptyForm = () => ({
  nom: '',
  categorie: 'poisson' as Categorie,
  image: '',
  resume: '',
  nutri_score: 'B' as NutriScore,
  glucides_totaux: 0,
  calories: 0,
  ingredients: [{ nom: '', quantite: 100, unite: 'g', glucides: 0 }],
  etapes: [''],
  conseil: '',
});

const categories: { id: Categorie; label: string }[] = [
  { id: 'poisson', label: 'Poisson' },
  { id: 'viande', label: 'Viande' },
  { id: 'volaille', label: 'Volaille' },
  { id: 'vegetarien', label: 'Végétarien' },
];

const nutriScores: NutriScore[] = ['A', 'B', 'C', 'D', 'E'];

export default function AdminPlats() {
  const [dbPlats, setDbPlats] = useState<ApiPlat[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const loadPlats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAllPlats();
      setDbPlats(data);
    } catch (err) {
      setError(`Impossible de charger les recettes : ${err instanceof Error ? err.message : 'Erreur inconnue'}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlats();
  }, [loadPlats]);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.nom.trim()) e.nom = 'Le nom est requis';
    if (!form.resume.trim()) e.resume = 'Le résumé est requis';
    if (form.ingredients.some((i) => !i.nom.trim())) e.ingredients = 'Tous les ingrédients doivent avoir un nom';
    if (form.etapes.some((et) => !et.trim())) e.etapes = 'Toutes les étapes doivent être renseignées';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await createPlat({
        ...form,
        conseil: form.conseil || undefined,
      });
      await loadPlats();
      setShowForm(false);
      setForm(emptyForm());
    } catch (err) {
      setError(`Erreur lors de la sauvegarde : ${err instanceof Error ? err.message : 'Erreur inconnue'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deletePlat(id);
      await loadPlats();
      setDeleteConfirm(null);
    } catch {
      setError('Erreur lors de la suppression.');
    }
  };

  const addIngredient = () => setForm({ ...form, ingredients: [...form.ingredients, { nom: '', quantite: 100, unite: 'g', glucides: 0 }] });
  const removeIngredient = (i: number) => setForm({ ...form, ingredients: form.ingredients.filter((_, idx) => idx !== i) });
  const updateIngredient = (i: number, field: string, value: string | number) => {
    const updated = form.ingredients.map((ing, idx) => idx === i ? { ...ing, [field]: value } : ing);
    setForm({ ...form, ingredients: updated });
  };

  const addEtape = () => setForm({ ...form, etapes: [...form.etapes, ''] });
  const removeEtape = (i: number) => setForm({ ...form, etapes: form.etapes.filter((_, idx) => idx !== i) });
  const updateEtape = (i: number, value: string) => {
    const updated = form.etapes.map((et, idx) => idx === i ? value : et);
    setForm({ ...form, etapes: updated });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Gestion des Recettes</h2>
          <p className="text-gray-500 text-sm mt-0.5">
            {mockPlats.length} de base + {dbPlats.length} en base de données
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => { setShowForm(true); setForm(emptyForm()); }}
            className="flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-5 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap"
          >
            <AddIcon />
            Ajouter une recette
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-5 text-sm">
          <ErrorIcon />
          {error}
          <button onClick={() => setError(null)} className="ml-auto cursor-pointer">
            <CloseIcon />
          </button>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6 mb-8">
          <h3 className="font-bold text-gray-900 mb-5">Nouvelle recette</h3>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du plat *</label>
                <input
                  type="text" value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })}
                  placeholder="Ex: Saumon grillé aux légumes"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white"
                />
                {errors.nom && <p className="text-red-500 text-xs mt-1">{errors.nom}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                <select
                  value={form.categorie}
                  onChange={(e) => setForm({ ...form, categorie: e.target.value as Categorie })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white cursor-pointer"
                >
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">URL de l'image</label>
                <input
                  type="url" value={form.image}
                  onChange={(e) => setForm({ ...form, image: e.target.value })}
                  placeholder="https://..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Résumé *</label>
                <textarea
                  value={form.resume}
                  onChange={(e) => setForm({ ...form, resume: e.target.value })}
                  placeholder="Description courte du plat..."
                  rows={2} maxLength={300}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white resize-none"
                />
                {errors.resume && <p className="text-red-500 text-xs mt-1">{errors.resume}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nutri-Score</label>
                <div className="flex gap-2">
                  {nutriScores.map((s) => (
                    <button
                      key={s} type="button"
                      onClick={() => setForm({ ...form, nutri_score: s })}
                      className={`w-10 h-10 flex items-center justify-center rounded-lg font-bold text-sm cursor-pointer transition-all ${
                        form.nutri_score === s
                          ? s === 'A' ? 'bg-emerald-500 text-white' : s === 'B' ? 'bg-lime-500 text-white' : s === 'C' ? 'bg-yellow-500 text-white' : s === 'D' ? 'bg-orange-500 text-white' : 'bg-red-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Glucides totaux (g)</label>
                <input
                  type="number" min={0} value={form.glucides_totaux}
                  onChange={(e) => setForm({ ...form, glucides_totaux: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Calories (kcal)</label>
                <input
                  type="number" min={0} value={form.calories}
                  onChange={(e) => setForm({ ...form, calories: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conseil DT1 <span className="text-gray-400 font-normal">(facultatif)</span>
                </label>
                <textarea
                  value={form.conseil}
                  onChange={(e) => setForm({ ...form, conseil: e.target.value })}
                  placeholder="Conseil spécifique pour les diabétiques de type 1..."
                  rows={2} maxLength={300}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-400 bg-white resize-none"
                />
              </div>
            </div>

            {/* Ingrédients */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Ingrédients *</label>
                <button type="button" onClick={addIngredient} className="text-xs text-teal-600 hover:text-teal-500 font-medium cursor-pointer flex items-center gap-1">
                  <PlusIcon /> Ajouter
                </button>
              </div>
              {errors.ingredients && <p className="text-red-500 text-xs mb-2">{errors.ingredients}</p>}
              <div className="space-y-2">
                {form.ingredients.map((ing, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input type="text" value={ing.nom} onChange={(e) => updateIngredient(i, 'nom', e.target.value)} placeholder="Nom" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-teal-400 bg-white" />
                    <input type="number" value={ing.quantite} onChange={(e) => updateIngredient(i, 'quantite', parseInt(e.target.value) || 0)} placeholder="Qté" className="w-20 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-teal-400 bg-white" />
                    <input type="text" value={ing.unite} onChange={(e) => updateIngredient(i, 'unite', e.target.value)} placeholder="g/ml" className="w-16 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-teal-400 bg-white" />
                    <input type="number" value={ing.glucides} onChange={(e) => updateIngredient(i, 'glucides', parseFloat(e.target.value) || 0)} placeholder="G" className="w-16 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-teal-400 bg-white" />
                    {form.ingredients.length > 1 && (
                      <button type="button" onClick={() => removeIngredient(i)} className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer">
                        <XIcon />
                      </button>
                    )}
                  </div>
                ))}
                <p className="text-xs text-gray-400">Nom | Quantité | Unité | Glucides (g)</p>
              </div>
            </div>

            {/* Étapes */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Étapes de préparation *</label>
                <button type="button" onClick={addEtape} className="text-xs text-teal-600 hover:text-teal-500 font-medium cursor-pointer flex items-center gap-1">
                  <PlusIcon /> Ajouter
                </button>
              </div>
              {errors.etapes && <p className="text-red-500 text-xs mb-2">{errors.etapes}</p>}
              <div className="space-y-2">
                {form.etapes.map((etape, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <div className="w-6 h-6 flex items-center justify-center bg-teal-100 text-teal-700 text-xs font-bold rounded-full shrink-0 mt-2">{i + 1}</div>
                    <textarea
                      value={etape} onChange={(e) => updateEtape(i, e.target.value)}
                      placeholder={`Étape ${i + 1}...`} rows={2} maxLength={300}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-teal-400 bg-white resize-none"
                    />
                    {form.etapes.length > 1 && (
                      <button type="button" onClick={() => removeEtape(i)} className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer mt-1">
                        <XIcon />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit" disabled={saving}
                className="flex-1 bg-teal-500 hover:bg-teal-400 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {saving && <LoaderIcon />}
                Ajouter la recette
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setForm(emptyForm()); setErrors({}); }}
                className="px-6 border border-gray-200 text-gray-600 hover:bg-gray-50 font-semibold py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <LoaderIcon />
          <span className="text-sm ml-3">Chargement depuis la base de données...</span>
        </div>
      )}

      {/* DB plats */}
      {!loading && dbPlats.length > 0 && (
        <div className="mb-8">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2 text-teal-600">
            <DatabaseIcon />
            Recettes en base de données ({dbPlats.length})
          </h3>
          <div className="space-y-3">
            {dbPlats.map((plat) => (
              <div key={plat.id} className="flex items-center gap-4 bg-white border border-gray-100 rounded-xl p-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 bg-gray-100 flex items-center justify-center">
                  {plat.image ? (
                    <img src={plat.image} alt={plat.nom} className="w-full h-full object-cover object-top" />
                  ) : (
                    <ImageIcon />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm">{plat.nom}</span>
                    <span className="bg-teal-50 text-teal-700 text-xs px-2 py-0.5 rounded-full capitalize">{plat.categorie}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${plat.nutri_score === 'A' ? 'bg-emerald-100 text-emerald-700' : plat.nutri_score === 'B' ? 'bg-lime-100 text-lime-700' : 'bg-amber-100 text-amber-700'}`}>
                      {plat.nutri_score}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{plat.resume}</p>
                  <div className="flex gap-3 mt-1 text-xs text-gray-400">
                    <span>{plat.glucides_totaux}g glucides</span>
                    <span>{plat.calories} kcal</span>
                    <span>{plat.ingredients.length} ingrédients</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {deleteConfirm === plat.id ? (
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleDelete(plat.id)} className="text-xs bg-red-500 text-white px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap">Confirmer</button>
                      <button onClick={() => setDeleteConfirm(null)} className="text-xs border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap">Annuler</button>
                    </div>
                  ) : (
                    <button onClick={() => setDeleteConfirm(plat.id)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer">
                      <DeleteIcon />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mock plats (read-only) */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2 text-gray-400">
          <FileCodeIcon />
          Recettes intégrées ({mockPlats.length}) — lecture seule
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {mockPlats.map((plat) => (
            <div key={plat.id} className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl p-3">
              <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0">
                <img src={plat.image} alt={plat.nom} className="w-full h-full object-cover object-top" />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-gray-800 text-sm truncate">{plat.nom}</p>
                <p className="text-xs text-gray-500">{plat.glucidesTotaux}g G · {plat.nutriScore}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}